import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  cacheKeys,
  FeatureName,
  Group,
  PermissionValues,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentAccount,
  useCurrentWorkspace,
  useCustomMutation,
  useIsFeatureEnabled,
  useRoutineFilter,
  WorkspaceType,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import {
  Colors,
  ColumnData,
  RoutineExecutionProps,
  RoutineModal,
  RoutineModules,
  SelectionStatus,
  SlideLayout,
  Spacing,
  ViewMode,
} from '@gripp/shared-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, StyleSheet, Text, View } from 'react-native';
import { Button } from 'react-native-paper';
import { useNavigate } from 'react-router-dom';

import { ModalButton } from '../../shared/components/modalButton';
import { CopyToWorkspaceButton } from './copyToWorkspaceButton';
import { DeleteRoutineButton } from './deleteRoutineButton';
import { RoutineAssetsForm } from './routineAssetsForm';
import { buildRoutinesListFilter } from './buildRoutinesListFilter';
import { Routines as RoutinesBaseOld } from './routines-old';
import { RoutinesFilters } from './routinesFilters';
import { RoutineList } from './routinesList';
import { RoutinesGroupsPicker } from './routinesGroupsPicker';
import { RoutinesSelectionPanelMain } from './routinesSelectionPanel';

export const Routines = () => {
  const isCropCircleEnable = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnable) {
    return <RoutinesBase />;
  }

  return <RoutinesBaseOld />;
};

export const RoutinesBase = ({
  rightColumn: rightColumnOverride,
  viewMode: viewModeOverride,
}: {
  rightColumn?: ColumnData;
  viewMode?: ViewMode;
} = {}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { createGroup } = useCreateGroup();
  const { data: routineFilter } = useRoutineFilter();
  const { account } = useCurrentAccount();

  type SelectedRoutineRow = {
    id: string;
    groups?: Array<{ id: string; name: string }>;
  };

  const [rowsSelected, setRowsSelected] = useState<SelectedRoutineRow[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [isGroupsPickerOpen, setIsGroupsPickerOpen] = useState(false);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');
  const [openRoutinePreviewModal, setOpenRoutinePreviewModal] = useState(false);
  const [executeModalProps, setExecuteModalProps] = useState<
    RoutineExecutionProps | undefined
  >();

  const impersonatingWorkspace = useCurrentWorkspace(true);
  const currentWorkspace = useCurrentWorkspace();
  const workspace = useCurrentWorkspace(true);

  const showCheckboxColumn = useMemo(
    () => !!impersonatingWorkspace,
    [impersonatingWorkspace]
  );

  const filter = useMemo(() => {
    return buildRoutinesListFilter(routineFilter, impersonatingWorkspace?.id);
  }, [routineFilter, impersonatingWorkspace?.id]);

  const updateRowsSelected = useCallback((rows: Array<any>) => {
    setRowsSelected(
      rows.map((row) => ({ id: row.id, groups: row.groups }))
    );
  }, []);

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );
  const hasSelection = rowsSelected.length > 0;

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);

  const selectedIdsSet = useMemo(
    () => new Set(rowsSelectedIds),
    [rowsSelectedIds]
  );
  const isRowSelected = useCallback(
    (row: { id?: string }) => (row?.id ? selectedIdsSet.has(row.id) : false),
    [selectedIdsSet]
  );

  const { groups: availableGroups } = useAllWorkspaceGroups(workspace?.id);

  const { mutateAsync: assignRoutinesToGroups } = useCustomMutation({
    query: ASSIGN_ROUTINES_TO_GROUPS,
  });

  // Seed groups panel from selected rows (same logic as People/Assets):
  //   - 1 selected → its groups pre-selected.
  //   - 2+ with exactly same groups → pre-selected.
  //   - 2+ with any difference → empty.
  //   - 0 selected → clear and close picker.
  // Match by name and re-hydrate real IDs from `availableGroups`.
  useEffect(() => {
    if (!hasSelection) {
      setSelectedGroups([]);
      setIsGroupsPickerOpen(false);
      return;
    }

    const [first, ...rest] = rowsSelected;
    const firstNames = (first.groups ?? [])
      .map((g) => g.name)
      .filter((n): n is string => !!n)
      .sort();

    const allSame = rest.every((row) => {
      const names = (row.groups ?? [])
        .map((g) => g.name)
        .filter((n): n is string => !!n)
        .sort();
      return (
        names.length === firstNames.length &&
        names.every((n, i) => n === firstNames[i])
      );
    });

    if (!allSame) {
      setSelectedGroups([]);
      return;
    }

    const nextSelected = firstNames
      .map((name) => availableGroups.find((g) => g.name === name))
      .filter((g): g is Group => !!g)
      .map((g) => ({ id: g.id, name: g.name }));

    setSelectedGroups(nextSelected);
  }, [rowsSelected, availableGroups, hasSelection]);

  const handleGroupsChange = useCallback(
    async (nextGroups: Group[]) => {
      const previousIds = new Set(selectedGroups.map((g) => g.id));
      const added = nextGroups.filter((g) => !previousIds.has(g.id));

      setSelectedGroups(nextGroups);

      if (added.length === 0 || !workspace?.id) return;

      await assignRoutinesToGroups({
        input: {
          routineIds: rowsSelectedIds,
          groupIds: added.map((g) => g.id),
          workspaceId: workspace.id,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('routine').all,
      });
    },
    [
      selectedGroups,
      rowsSelectedIds,
      workspace?.id,
      assignRoutinesToGroups,
      queryClient,
    ]
  );

  const isGlobalAdmin = useMemo(
    () => !!(account && account.hasPermission(PermissionValues.globalAdmin)),
    [account]
  );

  const canViewRoutines = useMemo(
    () =>
      !!(account && account.hasPermission(PermissionValues.routinespage.read)),
    [account]
  );

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const AssetsButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="qrcode"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.assetsCount)}
          </View>
        }
        modalComponent={({ onComplete }: { onComplete: () => void }) => {
          return (
            <RoutineAssetsForm
              routineId={data.routineId}
              title={data.name}
              showCheckboxColumn={showCheckboxColumn}
              onComplete={onComplete}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const AddonsButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="shape-outline"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.addonsCount ?? 0)}
          </View>
        }
        modalComponent={({ onComplete }: { onComplete: () => void }) => {
          return (
            <RoutineAssetsForm
              routineId={data.routineId}
              title={data.name}
              showCheckboxColumn={showCheckboxColumn}
              onComplete={onComplete}
              initialTab="addons"
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const rightFormButton = executeModalProps?.routine && isGlobalAdmin && (
    <Button
      style={styles.button}
      labelStyle={styles.buttonLabel}
      onPress={() => {
        navigate(`${executeModalProps?.routine?.id}/edit`);
      }}
      buttonColor={Colors.primary}
    >
      {t('topNav.modify')}
    </Button>
  );

  const footer = executeModalProps?.routine && isGlobalAdmin && (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
      <DeleteRoutineButton
        onSelected={() => setOpenRoutinePreviewModal(false)}
        workspaceRoutine={executeModalProps.routine}
      />
      <CopyToWorkspaceButton
        onSelected={() => setOpenRoutinePreviewModal(false)}
        workspaceRoutine={executeModalProps.routine}
      />
    </View>
  );

  const handleRoutineRowClick = (row: any) => {
    const {
      assetsCount,
      allAssetsCount,
      addonsCount,
      submissionsCount,
      lastSubmitted,
      updatedAt,
      ...rest
    } = row || {};

    setOpenRoutinePreviewModal(true);
    setExecuteModalProps({
      workspaceAsset: {
        workspace: currentWorkspace,
      },
      module: RoutineModules?.[row.type],
      routine: { ...rest },
      preview: true,
    });
  };

  const filterComponent = <RoutinesFilters />;

  const columnComponent = (
    <View style={styles.container}>
      <RoutineList
        filter={filter}
        updateRowsSelected={updateRowsSelected}
        isRowSelected={isRowSelected}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
        showCheckboxColumn={showCheckboxColumn}
        showImpersonateWorkspace={canViewRoutines}
        assetsButton={AssetsButton}
        addonsButton={
          currentWorkspace?.type === WorkspaceType.Oem
            ? AddonsButton
            : undefined
        }
        onRowClick={handleRoutineRowClick}
        isProducerWorkspace={currentWorkspace?.type === WorkspaceType.Producer}
      />
      {openRoutinePreviewModal && executeModalProps && (
        <RoutineModal
          {...executeModalProps}
          visible={openRoutinePreviewModal}
          onCanceled={() => {
            setOpenRoutinePreviewModal(false);
            setExecuteModalProps(undefined);
          }}
          onCompleted={async () => {
            setOpenRoutinePreviewModal(false);
          }}
          rightFormButton={rightFormButton}
          footer={footer}
          showPortal
        />
      )}
    </View>
  );

  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection || !workspace?.id) return undefined;

    const count = rowsSelected.length;
    const title =
      count === 1
        ? t('routine.rowHeader.routineSelected', {
            defaultValue: '1 routine selected',
          })
        : t('routine.rowHeader.routinesSelected', {
            count,
            defaultValue: `${count} routines selected`,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: (
        <RoutinesSelectionPanelMain
          routineIds={rowsSelectedIds}
          workspaceId={workspace.id}
          selectedGroups={selectedGroups}
          onGroupsChange={handleGroupsChange}
          onCreateGroup={createGroup}
          onOpenPicker={() => setIsGroupsPickerOpen(true)}
        />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [
    hasSelection,
    rowsSelected,
    rowsSelectedIds,
    workspace?.id,
    selectedGroups,
    handleGroupsChange,
    createGroup,
    clearSelection,
    t,
    i18n,
  ]);

  const thirdColumn = useMemo<ColumnData | undefined>(() => {
    if (!isGroupsPickerOpen || !hasSelection || !workspace?.id) {
      return undefined;
    }

    return {
      title: 'asset.groups',
      mainComponent: (
        <RoutinesGroupsPicker
          selectedGroups={selectedGroups}
          onGroupsChange={handleGroupsChange}
          onCreateGroup={createGroup}
          workspaceId={workspace.id}
        />
      ),
      showBackButton: true,
      onBackButtonPress: () => setIsGroupsPickerOpen(false),
      disableScroll: true,
    };
  }, [
    isGroupsPickerOpen,
    hasSelection,
    workspace?.id,
    selectedGroups,
    handleGroupsChange,
    createGroup,
  ]);

  const resolvedRightColumn = selectionRightColumn ?? rightColumnOverride;
  const resolvedViewMode =
    viewModeOverride ??
    (hasSelection || rightColumnOverride ? ViewMode.DUAL : ViewMode.SINGLE);

  return (
    <SlideLayout
      leftColumn={{
        title: 'routine.list.routines',
        mainComponent: columnComponent,
        headerComponent: filterComponent,
        disableScroll: true,
      }}
      rightColumn={resolvedRightColumn}
      thirdColumn={thirdColumn}
      viewMode={resolvedViewMode}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  searchContainer: {
    width: 390,
    marginRight: 20,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: 2,
  },
  countText: {
    fontSize: 16,
  },
  cellText: {
    fontSize: 16,
    fontWeight: '500',
    flexWrap: 'nowrap',
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 10,
  },
  button: {
    borderRadius: 8,
    width: '100%',
    maxWidth: 100,
    height: 44,
    justifyContent: 'center',
    marginRight: Spacing.baseMargin.base,
  },
  buttonLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.white,
  },
});

const ASSIGN_ROUTINES_TO_GROUPS = `
  mutation AssignRoutinesToGroups($input: AssignRoutinesToGroupsInput!) {
    routine {
      assignRoutinesToGroups(input: $input) {
        success
        message
      }
    }
  }
`;
